
import { useCallback, useRef } from 'react';
import { PrayerChoiceService, type ActionType, type ChoiceMethod } from '@/services/PrayerChoiceService';
import { PrayerAnalyticsService, type EngagementType } from '@/services/PrayerAnalyticsService';
import { useAuth } from '@/contexts/auth';
import { criticalLogger } from '@/utils/logger';

interface PrayerChoiceAnalyticsHook {
  trackChoiceDialogOpened: (prayerId: string, sourceContext?: string) => void;
  trackChoiceSelected: (prayerId: string, actionType: ActionType, sourceContext?: string) => void;
  recordChoiceAction: (prayerId: string, actionType: ActionType, choiceMethod: ChoiceMethod) => Promise<string | null>;
  trackMethodSelected: (prayerId: string, method: 'ai_assistant' | 'manual_prayer') => void;
  recordPrayedFor: (prayerId: string, choiceActionId: string, method: 'ai_assistant' | 'manual') => Promise<void>;
}

export function usePrayerChoiceAnalytics(): PrayerChoiceAnalyticsHook {
  const { user } = useAuth();
  const recentEvents = useRef(new Set<string>());

  const trackChoiceDialogOpened = useCallback((prayerId: string, sourceContext = 'unknown') => {
    const eventKey = `dialog-${prayerId}-${sourceContext}`;
    if (recentEvents.current.has(eventKey)) return;
    
    recentEvents.current.add(eventKey);
    setTimeout(() => recentEvents.current.delete(eventKey), 2000);
    
    PrayerAnalyticsService.trackEngagement({
      prayerId,
      engagementType: 'choice_dialog_opened',
      sourceContext,
      metadata: { timestamp: new Date().toISOString() }
    });
  }, []);

  const trackChoiceSelected = useCallback((prayerId: string, actionType: ActionType, sourceContext = 'choice_dialog') => {
    const eventKey = `choice-${prayerId}-${actionType}`;
    if (recentEvents.current.has(eventKey)) return;
    
    recentEvents.current.add(eventKey);
    setTimeout(() => recentEvents.current.delete(eventKey), 2000);
    
    const engagementType: EngagementType = actionType === 'i_need_prayer' 
      ? 'i_need_prayer_selected' 
      : 'i_want_to_pray_selected';

    PrayerAnalyticsService.trackEngagement({
      prayerId,
      engagementType,
      sourceContext,
      metadata: { 
        actionType,
        timestamp: new Date().toISOString()
      }
    });
  }, []);

  const recordChoiceAction = useCallback(async (
    prayerId: string, 
    actionType: ActionType, 
    choiceMethod: ChoiceMethod
  ): Promise<string | null> => {
    if (!user) return null;

    try {
      const result = await PrayerChoiceService.recordChoiceAction({
        prayerId,
        actionType,
        choiceMethod,
        metadata: {
          timestamp: new Date().toISOString(),
          userId: user.id
        }
      });

      return result.id;
    } catch (error) {
      criticalLogger.error('Error recording choice action:', error);
      return null;
    }
  }, [user]);

  const trackMethodSelected = useCallback((prayerId: string, method: 'ai_assistant' | 'manual_prayer') => {
    const eventKey = `method-${prayerId}-${method}`;
    if (recentEvents.current.has(eventKey)) return;
    
    recentEvents.current.add(eventKey);
    setTimeout(() => recentEvents.current.delete(eventKey), 2000);
    
    const engagementType: EngagementType = method === 'ai_assistant' 
      ? 'ai_assistant_opened' 
      : 'manual_prayer_completed';

    PrayerAnalyticsService.trackEngagement({
      prayerId,
      engagementType,
      sourceContext: 'choice_dialog',
      metadata: { 
        method,
        timestamp: new Date().toISOString()
      }
    });
  }, []);

  const recordPrayedFor = useCallback(async (
    prayerId: string, 
    choiceActionId: string, 
    method: 'ai_assistant' | 'manual'
  ) => {
    try {
      await PrayerChoiceService.recordPrayedFor(prayerId, choiceActionId, method);
    } catch (error) {
      criticalLogger.error('Error recording prayed for:', error);
    }
  }, []);

  return {
    trackChoiceDialogOpened,
    trackChoiceSelected,
    recordChoiceAction,
    trackMethodSelected,
    recordPrayedFor
  };
}
